import Chart from 'chart.js/auto';
import 'chartjs-adapter-moment';

// Alerts, key metrics, clinical documentation dashboards
const organizationDashboardGraphs = () => {
  const clinicalDocumentationDashboard = document.getElementById('clinical-documentation-dashboard');
  const alertsDashboard = document.getElementById('alerts-dashboard');
  const keyMetricsDashboard = document.getElementById('key-metrics-dashboard');
  const agingReportChart = document.getElementById('aging-chart');
  const agingChartDataDiv = document.getElementById('aging-chart-data');
  const revenueTrendsStackedChart = document.getElementById('revenue-trends-stacked');
  const revenueDataStackedDiv = document.getElementById('revenue-data-stacked');

  if (revenueDataStackedDiv) {
    const revenueDataStacked = JSON.parse(revenueDataStackedDiv.dataset.attrs);
    const revenueLabels = [];
    const grossRevenueData = [];
    const expensesData = [];
    const netIncomeData = [];

    for (i = 0; i < revenueDataStacked.length; i += 1) {
      const data = revenueDataStacked[i];
      revenueLabels.push(data.month);
      grossRevenueData.push(data.gross_revenue);
      expensesData.push(data.expenses);
      netIncomeData.push(data.net_income);
    }

    const revenueTrendsStacked = new Chart(revenueTrendsStackedChart, {
      type: 'bar',
      data: {
        labels: revenueLabels,
        datasets: [
          {
            label: 'Net Income',
            data: netIncomeData,
            backgroundColor: 'rgba(75, 192, 192, 0.8)',
            borderColor: 'rgba(75, 192, 192, 1)',
            borderWidth: 1,
            stack: 'revenue'
          },
          {
            label: 'Expenses',
            data: expensesData,
            backgroundColor: 'rgba(255, 99, 132, 0.8)',
            borderColor: 'rgba(255, 99, 132, 1)',
            borderWidth: 1,
            stack: 'revenue'
          }
        ],
      },
      options: {
        responsive: true,
        interaction: {
          intersect: false,
          mode: 'index',
          axis: 'x',
        },
        plugins: {
          tooltip: {
            position: 'nearest',
            callbacks: {
              afterTitle(context) {
                // Calculate gross revenue for the tooltip
                const dataIndex = context[0].dataIndex;
                const grossRevenue = grossRevenueData[dataIndex];
                return `Gross Revenue: $${grossRevenue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
              },
              label(context) {
                let label = context.dataset.label || '';
                if (label) {
                  label += ': ';
                }
                label += '$' + context.parsed.y.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                return label;
              }
            }
          },
          legend: {
            display: true,
          },
        },
        scales: {
          x: {
            stacked: true
          },
          y: {
            stacked: true,
            ticks: {
              callback(value) {
                return '$' + value.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
              },
            },
          },
        },
      },
    });
  }
  if (agingReportChart && agingChartDataDiv) {
    const chartData = JSON.parse(agingChartDataDiv.dataset.attrs);

    // Configure datasets with colors
    const datasets = chartData.datasets.map((dataset, index) => {
      const colors = [
        { bg: 'rgba(75, 192, 192, 0.8)', border: 'rgba(75, 192, 192, 1)' },    // Patient - Teal
        { bg: 'rgba(41, 72, 160, 0.8)', border: 'rgba(41, 72, 160, 1)' },      // Insurance - Blue
        { bg: 'rgba(255, 159, 64, 0.8)', border: 'rgba(255, 159, 64, 1)' }     // Facility - Orange
      ];

      return {
        label: dataset.label,
        data: dataset.data,
        backgroundColor: colors[index].bg,
        borderColor: colors[index].border,
        borderWidth: 1,
      };
    });

    const agingChart = new Chart(agingReportChart, {
      type: 'bar',
      data: {
        labels: chartData.labels,
        datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: 'index',
          axis: 'x',
        },
        plugins: {
          title: {
            display: true,
            text: 'Aging report by payer type and days outstanding'
          },
          tooltip: {
            position: 'nearest',
            callbacks: {
              label(context) {
                let label = context.dataset.label || '';
                if (label) {
                  label += ': ';
                }
                label += new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                }).format(context.parsed.y);
                return label;
              },
            },
          },
          legend: {
            display: true,
            position: 'top',
          },
        },
        scales: {
          x: {
            title: {
              display: true,
              text: 'Days outstanding',
            },
          },
          y: {
            title: {
              display: true,
              text: chartData.amountType || 'Amount',
            },
            ticks: {
              callback(value) {
                return new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }).format(value);
              },
            },
          },
        },
      },
    });
  }

  let i;
  if (clinicalDocumentationDashboard) {
    const visitNotesTrendsChart = document.getElementById('visit-notes-trends');
    const visitNotesDataDiv = document.getElementById('visit-notes-data');
    if (visitNotesDataDiv) {
      const visitNotesData = JSON.parse(visitNotesDataDiv.dataset.attrs);
      const visitNotesLabels = [];
      const visitNotesDataTotal = [];

      for (i = 0; i < visitNotesData.length; i += 1) {
        const data = visitNotesData[i];
        visitNotesLabels.push(data[0]);
        visitNotesDataTotal.push(data[1]);
      }

      const visitNotesTrends = new Chart(visitNotesTrendsChart, {
        type: 'bar',
        data: {
          labels: visitNotesLabels,
          datasets: [
            {
              label: 'Visit Notes',
              data: visitNotesDataTotal,
              backgroundColor: 'rgba(41, 72, 160, 0.8)',
              borderColor: 'rgba(255, 99, 132, 1)',
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          interaction: {
            intersect: false,
            mode: 'index',
            axis: 'x',
          },
          plugins: {
            tooltip: {
              position: 'nearest',
            },
            legend: {
              display: false,
            },
          },
          scales: {
            y: {
              ticks: {
                callback(value) {
                  return value.toLocaleString();
                },
              },
            },
          },
        },
      });
    }

    const evaluationsTrendsChart = document.getElementById('evaluations-trends');
    const evaluationsDataDiv = document.getElementById('evaluations-data');
    if (evaluationsDataDiv) {
      const evaluationsData = JSON.parse(evaluationsDataDiv.dataset.attrs);
      const evaluationsLabels = [];
      const evaluationsDataTotal = [];

      for (i = 0; i < evaluationsData.length; i += 1) {
        const data = evaluationsData[i];
        evaluationsLabels.push(data[0]);
        evaluationsDataTotal.push(data[1]);
      }

      const evaluationsTrends = new Chart(evaluationsTrendsChart, {
        type: 'bar',
        data: {
          labels: evaluationsLabels,
          datasets: [
            {
              label: 'Evaluations',
              data: evaluationsDataTotal,
              backgroundColor: 'rgba(41, 72, 160, 0.8)',
              borderColor: 'rgba(255, 99, 132, 1)',
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          interaction: {
            intersect: false,
            mode: 'index',
            axis: 'x',
          },
          plugins: {
            tooltip: {
              position: 'nearest',
            },
            legend: {
              display: false,
            },
          },
          scales: {
            y: {
              ticks: {
                callback(value) {
                  return value.toLocaleString();
                },
              },
            },
          },
        },
      });
    }

    const assessmentsTrendsChart = document.getElementById('assessments-trends');
    const assessmentsDataDiv = document.getElementById('assessments-data');
    if (assessmentsDataDiv) {
      const assessmentsData = JSON.parse(assessmentsDataDiv.dataset.attrs);
      const assessmentsLabels = [];
      const assessmentsDataTotal = [];

      for (i = 0; i < assessmentsData.length; i += 1) {
        const data = assessmentsData[i];
        assessmentsLabels.push(data[0]);
        assessmentsDataTotal.push(data[1]);
      }

      const assessmentsTrends = new Chart(assessmentsTrendsChart, {
        type: 'bar',
        data: {
          labels: assessmentsLabels,
          datasets: [
            {
              label: 'Assessments',
              data: assessmentsDataTotal,
              backgroundColor: 'rgba(41, 72, 160, 0.8)',
              borderColor: 'rgba(255, 99, 132, 1)',
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          interaction: {
            intersect: false,
            mode: 'index',
            axis: 'x',
          },
          plugins: {
            tooltip: {
              position: 'nearest',
            },
            legend: {
              display: false,
            },
          },
          scales: {
            y: {
              ticks: {
                callback(value) {
                  return value.toLocaleString();
                },
              },
            },
          },
        },
      });
    }

    const pocsTrendsChart = document.getElementById('pocs-trends');
    const pocsDataDiv = document.getElementById('pocs-data');
    if (pocsDataDiv) {
      const pocsData = JSON.parse(pocsDataDiv.dataset.attrs);
      const pocsLabels = [];
      const pocsDataTotal = [];

      for (i = 0; i < pocsData.length; i += 1) {
        const data = pocsData[i];
        pocsLabels.push(data[0]);
        pocsDataTotal.push(data[1]);
      }

      const pocsTrends = new Chart(pocsTrendsChart, {
        type: 'bar',
        data: {
          labels: pocsLabels,
          datasets: [
            {
              label: 'POCs',
              data: pocsDataTotal,
              backgroundColor: 'rgba(41, 72, 160, 0.8)',
              borderColor: 'rgba(255, 99, 132, 1)',
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          interaction: {
            intersect: false,
            mode: 'index',
            axis: 'x',
          },
          plugins: {
            tooltip: {
              position: 'nearest',
            },
            legend: {
              display: false,
            },
          },
          scales: {
            y: {
              ticks: {
                callback(value) {
                  return value.toLocaleString();
                },
              },
            },
          },
        },
      });
    }

    const progressReportsTrendsChart = document.getElementById('progress-reports-trends');
    const progressReportsDataDiv = document.getElementById('progress-reports-data');
    if (progressReportsDataDiv) {
      const progressReportsData = JSON.parse(progressReportsDataDiv.dataset.attrs);
      const progressReportsLabels = [];
      const progressReportsDataTotal = [];

      for (i = 0; i < progressReportsData.length; i += 1) {
        const data = progressReportsData[i];
        progressReportsLabels.push(data[0]);
        progressReportsDataTotal.push(data[1]);
      }

      const progressReportsTrends = new Chart(progressReportsTrendsChart, {
        type: 'bar',
        data: {
          labels: progressReportsLabels,
          datasets: [
            {
              label: 'Progress Reports',
              data: progressReportsDataTotal,
              backgroundColor: 'rgba(41, 72, 160, 0.8)',
              borderColor: 'rgba(255, 99, 132, 1)',
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          interaction: {
            intersect: false,
            mode: 'index',
            axis: 'x',
          },
          plugins: {
            tooltip: {
              position: 'nearest',
            },
            legend: {
              display: false,
            },
          },
          scales: {
            y: {
              ticks: {
                callback(value) {
                  return value.toLocaleString();
                },
              },
            },
          },
        },
      });
    }

    const schoolPlansTrendsChart = document.getElementById('school-plans-trends');
    const schoolPlansDataDiv = document.getElementById('school-plans-data');
    if (schoolPlansDataDiv) {
      const schoolPlansData = JSON.parse(schoolPlansDataDiv.dataset.attrs);
      const schoolPlansLabels = [];
      const schoolPlansDataTotal = [];

      for (i = 0; i < schoolPlansData.length; i += 1) {
        const data = schoolPlansData[i];
        schoolPlansLabels.push(data[0]);
        schoolPlansDataTotal.push(data[1]);
      }

      const schoolPlansTrends = new Chart(schoolPlansTrendsChart, {
        type: 'bar',
        data: {
          labels: schoolPlansLabels,
          datasets: [
            {
              label: 'School plans',
              data: schoolPlansDataTotal,
              backgroundColor: 'rgba(41, 72, 160, 0.8)',
              borderColor: 'rgba(255, 99, 132, 1)',
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          interaction: {
            intersect: false,
            mode: 'index',
            axis: 'x',
          },
          plugins: {
            tooltip: {
              position: 'nearest',
            },
            legend: {
              display: false,
            },
          },
          scales: {
            y: {
              ticks: {
                callback(value) {
                  return value.toLocaleString();
                },
              },
            },
          },
        },
      });
    }

    const dischargesTrendsChart = document.getElementById('discharges-trends');
    const dischargesDataDiv = document.getElementById('discharges-data');
    if (dischargesDataDiv) {
      const dischargesData = JSON.parse(dischargesDataDiv.dataset.attrs);
      const dischargesLabels = [];
      const dischargesDataTotal = [];

      for (i = 0; i < dischargesData.length; i += 1) {
        const data = dischargesData[i];
        dischargesLabels.push(data[0]);
        dischargesDataTotal.push(data[1]);
      }

      const dischargesTrends = new Chart(dischargesTrendsChart, {
        type: 'bar',
        data: {
          labels: dischargesLabels,
          datasets: [
            {
              label: 'Discharges',
              data: dischargesDataTotal,
              backgroundColor: 'rgba(41, 72, 160, 0.8)',
              borderColor: 'rgba(255, 99, 132, 1)',
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          interaction: {
            intersect: false,
            mode: 'index',
            axis: 'x',
          },
          plugins: {
            tooltip: {
              position: 'nearest',
            },
            legend: {
              display: false,
            },
          },
          scales: {
            y: {
              ticks: {
                callback(value) {
                  return value.toLocaleString();
                },
              },
            },
          },
        },
      });
    }
  } else if (keyMetricsDashboard) {
    const patientsTrendsChart = document.getElementById('patients-trends');
    const patientsDataDiv = document.getElementById('patients-data');
    if (patientsDataDiv) {
      const patientsData = JSON.parse(patientsDataDiv.dataset.attrs);
      const patientsLabels = [];
      const patientsDataTotal = [];

      for (i = 0; i < patientsData.length; i += 1) {
        const data = patientsData[i];
        patientsLabels.push(data[0]);
        patientsDataTotal.push(data[1]);
      }

      const patientsTrends = new Chart(patientsTrendsChart, {
        type: 'bar',
        data: {
          labels: patientsLabels,
          datasets: [
            {
              label: 'Patients',
              data: patientsDataTotal,
              backgroundColor: 'rgba(41, 72, 160, 0.8)',
              borderColor: 'rgba(255, 99, 132, 1)',
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          interaction: {
            intersect: false,
            mode: 'index',
            axis: 'x',
          },
          plugins: {
            tooltip: {
              position: 'nearest',
            },
            legend: {
              display: false,
            },
          },
          scales: {
            y: {
              ticks: {
                callback(value) {
                  return value.toLocaleString();
                },
              },
            },
          },
        },
      });
    }

    const visitsTrendsChart = document.getElementById('visits-trends');
    const visitsDataDiv = document.getElementById('visits-data');
    if (visitsDataDiv) {
      const visitsData = JSON.parse(visitsDataDiv.dataset.attrs);
      const visitsLabels = [];
      const visitsDataTotal = [];

      for (i = 0; i < visitsData.length; i += 1) {
        const data = visitsData[i];
        visitsLabels.push(data[0]);
        visitsDataTotal.push(data[1]);
      }

      const visitsTrends = new Chart(visitsTrendsChart, {
        type: 'bar',
        data: {
          labels: visitsLabels,
          datasets: [
            {
              label: 'Visits',
              data: visitsDataTotal,
              backgroundColor: 'rgba(41, 72, 160, 0.8)',
              borderColor: 'rgba(255, 99, 132, 1)',
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          interaction: {
            intersect: false,
            mode: 'index',
            axis: 'x',
          },
          plugins: {
            tooltip: {
              position: 'nearest',
            },
            legend: {
              display: false,
            },
          },
          scales: {
            y: {
              ticks: {
                callback(value) {
                  return value.toLocaleString();
                },
              },
            },
          },
        },
      });
    }

    const revenueTrendsChart = document.getElementById('revenue-trends');
    const revenueDataDiv = document.getElementById('revenue-data');
    if (revenueDataDiv) {
      const revenueData = JSON.parse(revenueDataDiv.dataset.attrs);
      const revenueLabels = [];
      const revenueDataTotal = [];

      for (i = 0; i < revenueData.length; i += 1) {
        const data = revenueData[i];
        revenueLabels.push(data[0]);
        revenueDataTotal.push(data[1]);
      }

      const revenueTrends = new Chart(revenueTrendsChart, {
        type: 'bar',
        data: {
          labels: revenueLabels,
          datasets: [
            {
              label: 'Revenue',
              data: revenueDataTotal,
              backgroundColor: 'rgba(41, 72, 160, 0.8)',
              borderColor: 'rgba(255, 99, 132, 1)',
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          interaction: {
            intersect: false,
            mode: 'index',
            axis: 'x',
          },
          plugins: {
            tooltip: {
              position: 'nearest',
            },
            legend: {
              display: false,
            },
          },
          scales: {
            y: {
              ticks: {
                callback(value) {
                  return value.toLocaleString();
                },
              },
            },
          },
        },
      });
    }
  } else if (alertsDashboard) {
    const overdueTrendsChart = document.getElementById('overdue-alerts-trends');
    const overdueDataDiv = document.getElementById('overdue-alerts-data');
    if (overdueDataDiv) {
      const overdueData = JSON.parse(overdueDataDiv.dataset.attrs);
      const overdueLabels = [];
      const overdueDataTotal = [];

      for (i = 0; i < overdueData.length; i += 1) {
        const data = overdueData[i];
        overdueLabels.push(data[0]);
        overdueDataTotal.push(data[1]);
      }

      const overdueTrends = new Chart(overdueTrendsChart, {
        type: 'bar',
        data: {
          labels: overdueLabels,
          datasets: [
            {
              label: 'Overdue Alerts',
              data: overdueDataTotal,
              backgroundColor: 'rgba(41, 72, 160, 0.8)',
              borderColor: 'rgba(255, 99, 132, 1)',
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          interaction: {
            intersect: false,
            mode: 'index',
            axis: 'x',
          },
          plugins: {
            tooltip: {
              position: 'nearest',
            },
            legend: {
              display: false,
            },
          },
          scales: {
            y: {
              ticks: {
                callback(value) {
                  return value.toLocaleString();
                },
              },
            },
          },
        },
      });
    }

    const comingdueTrendsChart = document.getElementById('coming-due-alerts-trends');
    const comingdueDataDiv = document.getElementById('coming-due-alerts-data');
    if (comingdueDataDiv) {
      const comingdueData = JSON.parse(comingdueDataDiv.dataset.attrs);
      const comingdueLabels = [];
      const comingdueDataTotal = [];

      for (i = 0; i < comingdueData.length; i += 1) {
        const data = comingdueData[i];
        comingdueLabels.push(data[0]);
        comingdueDataTotal.push(data[1]);
      }

      const comingdueTrends = new Chart(comingdueTrendsChart, {
        type: 'bar',
        data: {
          labels: comingdueLabels,
          datasets: [
            {
              label: 'Coming Due Alerts',
              data: comingdueDataTotal,
              backgroundColor: 'rgba(41, 72, 160, 0.8)',
              borderColor: 'rgba(255, 99, 132, 1)',
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          interaction: {
            intersect: false,
            mode: 'index',
            axis: 'x',
          },
          plugins: {
            tooltip: {
              position: 'nearest',
            },
            legend: {
              display: false,
            },
          },
          scales: {
            y: {
              ticks: {
                callback(value) {
                  return value.toLocaleString();
                },
              },
            },
          },
        },
      });
    }

    const missingTrendsChart = document.getElementById('missing-alerts-trends');
    const missingDataDiv = document.getElementById('missing-alerts-data');
    if (missingDataDiv) {
      const missingData = JSON.parse(missingDataDiv.dataset.attrs);
      const missingLabels = [];
      const missingDataTotal = [];

      for (i = 0; i < missingData.length; i += 1) {
        const data = missingData[i];
        missingLabels.push(data[0]);
        missingDataTotal.push(data[1]);
      }

      const missingTrends = new Chart(missingTrendsChart, {
        type: 'bar',
        data: {
          labels: missingLabels,
          datasets: [
            {
              label: 'Missing Alerts',
              data: missingDataTotal,
              backgroundColor: 'rgba(41, 72, 160, 0.8)',
              borderColor: 'rgba(255, 99, 132, 1)',
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          interaction: {
            intersect: false,
            mode: 'index',
            axis: 'x',
          },
          plugins: {
            tooltip: {
              position: 'nearest',
            },
            legend: {
              display: false,
            },
          },
          scales: {
            y: {
              ticks: {
                callback(value) {
                  return value.toLocaleString();
                },
              },
            },
          },
        },
      });
    }

    const problemTrendsChart = document.getElementById('problem-alerts-trends');
    const problemDataDiv = document.getElementById('problem-alerts-data');
    if (problemDataDiv) {
      const problemData = JSON.parse(problemDataDiv.dataset.attrs);
      const problemLabels = [];
      const problemDataTotal = [];

      for (i = 0; i < problemData.length; i += 1) {
        const data = problemData[i];
        problemLabels.push(data[0]);
        problemDataTotal.push(data[1]);
      }

      const problemTrends = new Chart(problemTrendsChart, {
        type: 'bar',
        data: {
          labels: problemLabels,
          datasets: [
            {
              label: 'Problem Alerts',
              data: problemDataTotal,
              backgroundColor: 'rgba(41, 72, 160, 0.8)',
              borderColor: 'rgba(255, 99, 132, 1)',
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          interaction: {
            intersect: false,
            mode: 'index',
            axis: 'x',
          },
          plugins: {
            tooltip: {
              position: 'nearest',
            },
            legend: {
              display: false,
            },
          },
          scales: {
            y: {
              ticks: {
                callback(value) {
                  return value.toLocaleString();
                },
              },
            },
          },
        },
      });
    }

    const otherTrendsChart = document.getElementById('other-alerts-trends');
    const otherDataDiv = document.getElementById('other-alerts-data');
    if (otherDataDiv) {
      const otherData = JSON.parse(otherDataDiv.dataset.attrs);
      const otherLabels = [];
      const otherDataTotal = [];

      for (i = 0; i < otherData.length; i += 1) {
        const data = otherData[i];
        otherLabels.push(data[0]);
        otherDataTotal.push(data[1]);
      }

      const otherTrends = new Chart(otherTrendsChart, {
        type: 'bar',
        data: {
          labels: otherLabels,
          datasets: [
            {
              label: 'Other Alerts',
              data: otherDataTotal,
              backgroundColor: 'rgba(41, 72, 160, 0.8)',
              borderColor: 'rgba(255, 99, 132, 1)',
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          interaction: {
            intersect: false,
            mode: 'index',
            axis: 'x',
          },
          plugins: {
            tooltip: {
              position: 'nearest',
            },
            legend: {
              display: false,
            },
          },
          scales: {
            y: {
              ticks: {
                callback(value) {
                  return value.toLocaleString();
                },
              },
            },
          },
        },
      });
    }

    const criticalTrendsChart = document.getElementById('critical-alerts-trends');
    const criticalDataDiv = document.getElementById('critical-alerts-data');
    if (criticalDataDiv) {
      const criticalData = JSON.parse(criticalDataDiv.dataset.attrs);
      const criticalLabels = [];
      const criticalDataTotal = [];

      for (i = 0; i < criticalData.length; i += 1) {
        const data = criticalData[i];
        criticalLabels.push(data[0]);
        criticalDataTotal.push(data[1]);
      }

      const criticalTrends = new Chart(criticalTrendsChart, {
        type: 'bar',
        data: {
          labels: criticalLabels,
          datasets: [
            {
              label: 'Stop Treatment Alerts',
              data: criticalDataTotal,
              backgroundColor: 'rgba(41, 72, 160, 0.8)',
              borderColor: 'rgba(255, 99, 132, 1)',
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          interaction: {
            intersect: false,
            mode: 'index',
            axis: 'x',
          },
          plugins: {
            tooltip: {
              position: 'nearest',
            },
            legend: {
              display: false,
            },
          },
          scales: {
            y: {
              ticks: {
                callback(value) {
                  return value.toLocaleString();
                },
              },
            },
          },
        },
      });
    }
  }
};

export default organizationDashboardGraphs;
